import { useState, useEffect } from 'react';
import { m, AnimatePresence, useReducedMotion } from 'framer-motion';
import { SloganLine } from './SloganLine';

// Cubic bezier ease matching the site's design system
const EASE_CINEMATIC: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

// Matches SloganLine's typography contract (Oswald 600 uppercase white) at 'lg' scale
const SLOGAN_TEXT_CLASS = 'font-en font-semibold text-4xl md:text-7xl uppercase text-white';

// Delay (ms) before the edge line starts drawing — fires once "FROM LIGHT" has finished typing
const LINE_DRAW_DELAY = 1000;

// Letter data with stable keys avoids re-keying on re-renders
const FROM_LIGHT = 'FROM LIGHT'.split('').map((char, i) => ({
  key: `fl-${char === ' ' ? 'sp' : char}-${i}`,
  char,
  delay: 0.3 + i * 0.045,
}));

const TO_CREATIVE = 'TO CREATIVE'.split('').map((char, i) => ({
  key: `tc-${char === ' ' ? 'sp' : char}-${i}`,
  char,
  delay: 1.6 + i * 0.045,
}));

export function Preloader() {
  const [isVisible, setIsVisible] = useState(true);
  const [isLineAnimated, setIsLineAnimated] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const dismissDelay = prefersReducedMotion ? 1000 : 3000;

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(false), dismissDelay);
    return () => clearTimeout(timer);
  }, [dismissDelay]);

  // Edge line draws once the "FROM LIGHT" typing pass has finished — matches SloganLine geometry
  useEffect(() => {
    if (prefersReducedMotion) return;
    const timer = setTimeout(() => setIsLineAnimated(true), LINE_DRAW_DELAY);
    return () => clearTimeout(timer);
  }, [prefersReducedMotion]);

  // Reduced-motion: render the final SloganLine frame instantly, then fade out
  if (prefersReducedMotion) {
    return (
      <AnimatePresence>
        {isVisible && (
          <m.div
            className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="w-[min(90vw,560px)]">
              <SloganLine size="lg" animate={false} />
            </div>
          </m.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <m.div
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
          exit={{
            opacity: 0,
            scale: 0.95,
          }}
          transition={{ duration: 0.5, ease: EASE_CINEMATIC }}
        >
          <div className="flex flex-col w-[min(90vw,600px)] gap-0">

            {/* Phase 1 — "FROM LIGHT" types in letter-by-letter, left-aligned */}
            <div
              className="flex overflow-hidden"
              aria-label="FROM LIGHT"
            >
              {FROM_LIGHT.map(({ key, char, delay }) => (
                <m.span
                  key={key}
                  className={`${SLOGAN_TEXT_CLASS}${char === ' ' ? ' w-[0.3em]' : ''}`}
                  initial={{ y: 48, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay, ease: EASE_CINEMATIC }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </m.span>
              ))}
            </div>

            {/* Phase 2 — edge line draws left-to-right after "FROM LIGHT" finishes typing */}
            <div className="w-full my-4 md:my-5">
              <div className={`edge-line${isLineAnimated ? ' is-animated' : ''}`} />
            </div>

            {/* Phase 3 — "TO CREATIVE" types in letter-by-letter, right-aligned */}
            <div
              className="flex overflow-hidden justify-end"
              aria-label="TO CREATIVE"
            >
              {TO_CREATIVE.map(({ key, char, delay }) => (
                <m.span
                  key={key}
                  className={`${SLOGAN_TEXT_CLASS}${char === ' ' ? ' w-[0.3em]' : ''}`}
                  initial={{ y: -40, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.4, delay, ease: EASE_CINEMATIC }}
                >
                  {char === ' ' ? '\u00A0' : char}
                </m.span>
              ))}
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
